import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, CheckCircle2, ChevronRight, Circle, Lock, LogIn, VideoOff } from 'lucide-react';
import { coursesApi } from '@/api/endpoints';
import { getApiErrorMessage } from '@/api/client';
import { mapDifficulty } from '@/api/mappers';
import type { CourseDetailResponse, LessonResponse } from '@/api/types';
import { CourseCurriculum } from '@/components/courses/CourseCurriculum';
import { LessonVideo } from '@/components/courses/LessonVideo';
import { PurchaseBox } from '@/components/courses/PurchaseBox';
import { useCourseAccess } from '@/components/courses/useCourseAccess';
import { useCourseProgress } from '@/components/courses/useCourseProgress';
import { DifficultyBadge } from '@/components/ui/DifficultyBadge';
import { MarkdownLite } from '@/components/ui/MarkdownLite';
import { useSeo } from '@/hooks/useSeo';
import { cn } from '@/lib/cn';
import { lessonSeoDescription, lessonSeoTitle } from '@/lib/seoText';
import {
  COURSES_INDEX_PATH,
  PAYWALL_CLASS,
  coursePath,
  lessonPath,
  lessonStructuredData,
} from '@/lib/structuredData';
import { useInitialData } from '@/ssr/initialData';
import { useAuthStore } from '@/store/auth';
import { useToastStore } from '@/store/toast';
import { NotFound } from './NotFound';

/**
 * Dars sahifasi: /courses/:slug/:lessonSlug. Prerender anonim ko'rinishni yozadi (qulflangan
 * darsda video/konspekt yo'q); kirgan foydalanuvchi uchun hydration'dan keyin qayta so'raladi.
 */
export function Lesson() {
  const { t } = useTranslation();
  const { slug = '', lessonSlug = '' } = useParams();
  const initial = useInitialData();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const pushToast = useToastStore((s) => s.push);

  const initialLesson =
    initial?.lesson?.courseSlug === slug && initial.lesson.slug === lessonSlug ? initial.lesson : null;
  const [lesson, setLesson] = useState<LessonResponse | null>(initialLesson);
  const [course, setCourse] = useState<CourseDetailResponse | null>(
    initial?.course?.slug === slug ? initial.course : null,
  );
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const { completed, replace } = useCourseProgress(slug);
  const { hasCourse } = useCourseAccess();

  // Qulf holati foydalanuvchiga bog'liq (kurs xaridi) — boshqa foydalanuvchi kirsa qayta so'raladi
  const viewerKey = `${user?.id ?? ''}`;
  const loadedKey = useRef<string | null>(initialLesson ? `${slug}/${lessonSlug}||` : null);

  useEffect(() => {
    const key = `${slug}/${lessonSlug}|${viewerKey}`;
    if (loadedKey.current === key) return;
    let cancelled = false;
    setNotFound(false);
    coursesApi
      .lesson(slug, lessonSlug)
      .then((res) => {
        if (cancelled) return;
        loadedKey.current = key;
        setLesson(res.data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, lessonSlug, viewerKey]);

  useEffect(() => {
    if (course?.slug === slug) return;
    let cancelled = false;
    coursesApi
      .detail(slug)
      .then((res) => {
        if (!cancelled) setCourse(res.data);
      })
      .catch(() => {
        /* yon panel ixtiyoriy */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const current = lesson && lesson.courseSlug === slug && lesson.slug === lessonSlug ? lesson : null;

  useSeo({
    title: current ? lessonSeoTitle(current.title) : 'Video dars — SQLMaster.uz',
    description: current ? lessonSeoDescription(current) : undefined,
    path: lessonPath(slug, lessonSlug),
    // Qulflangan darsda asosiy kontent yo'q — indekslanmaydi (sitemap ham faqat bepul darslarni beradi)
    noindex: current?.locked === true,
    jsonLd: current ? lessonStructuredData(current) : undefined,
  });

  if (notFound) return <NotFound />;
  if (!current) {
    return <div className="py-24 text-center text-content-secondary">{t('common.loading')}</div>;
  }

  const done = completed.has(current.slug);

  const toggleCompleted = async () => {
    setSaving(true);
    try {
      const res = await coursesApi.setCompleted(slug, current.slug, !done);
      replace(res.data.completedLessons);
    } catch (err) {
      pushToast(getApiErrorMessage(err, t('courses.completeError')), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-8xl px-4 py-6 sm:px-6 lg:px-8">
      <nav aria-label={t('problem.breadcrumb')} className="mb-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1.5 text-content-secondary">
          <li>
            <Link to={COURSES_INDEX_PATH} className="transition-colors hover:text-content">
              {t('nav.courses')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li>
            <Link to={coursePath(current.courseSlug)} className="transition-colors hover:text-content">
              {current.courseTitle}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" className="text-content">
            {current.title}
          </li>
        </ol>
      </nav>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <main className="min-w-0">
          {current.locked ? (
            <div
              className={cn(
                PAYWALL_CLASS,
                'flex aspect-video w-full flex-col items-center justify-center rounded-lg border border-[var(--medium)]/30 bg-[var(--medium)]/10 p-6 text-center',
              )}
            >
              <Lock className="mb-3 h-10 w-10 text-[var(--medium)]" />
              <h2 className="mb-2 text-lg font-semibold">{t('courses.lockedTitle')}</h2>
              <p className="mb-4 max-w-md text-sm text-content-secondary">{t('courses.lockedDesc')}</p>
              {course?.priceUzs != null ? (
                <PurchaseBox
                  kind="course"
                  title={course.title}
                  priceUzs={course.priceUzs}
                  owned={false}
                  className="justify-center text-left"
                />
              ) : (
                <Link to={coursePath(slug)} className="btn btn-primary px-5 py-2.5 text-sm font-semibold">
                  {t('courses.lockedCta')}
                </Link>
              )}
            </div>
          ) : current.loginRequired ? (
            <div
              data-login-required
              className="flex aspect-video w-full flex-col items-center justify-center rounded-lg border border-line bg-surface p-6 text-center"
            >
              <LogIn className="mb-3 h-10 w-10 text-brand-600" />
              <h2 className="mb-2 text-lg font-semibold">{t('courses.loginTitle')}</h2>
              <p className="mb-4 max-w-md text-sm text-content-secondary">{t('courses.loginDesc')}</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to={`/login?next=${encodeURIComponent(lessonPath(slug, current.slug))}`}
                  className="btn btn-primary px-5 py-2.5 text-sm font-semibold"
                >
                  {t('courses.loginCta')}
                </Link>
                <Link to="/register" className="btn btn-secondary px-5 py-2.5 text-sm font-semibold">
                  {t('courses.registerCta')}
                </Link>
              </div>
            </div>
          ) : current.videoProvider && current.videoRef ? (
            <LessonVideo
              key={`${current.slug}:${current.videoRef}`}
              provider={current.videoProvider}
              videoRef={current.videoRef}
              title={current.title}
              courseSlug={current.courseSlug}
              lessonSlug={current.slug}
              next={current.next ? { title: current.next.title, href: lessonPath(slug, current.next.slug) } : undefined}
            />
          ) : (
            <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg border border-line bg-surface text-content-secondary">
              <VideoOff className="h-8 w-8" />
              <p className="text-sm">{t('courses.noVideo')}</p>
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm text-content-muted">{current.moduleTitle}</p>
              <h1 className="mt-1 text-2xl font-bold">{current.title}</h1>
              {current.summary && <p className="mt-2 text-content-secondary">{current.summary}</p>}
            </div>
            {isAuthenticated && !current.locked && (
              <button
                type="button"
                onClick={toggleCompleted}
                disabled={saving}
                title={done ? t('courses.markIncomplete') : undefined}
                className={cn(
                  'btn inline-flex shrink-0 items-center gap-2 px-4 py-2 text-sm font-semibold',
                  done ? 'btn-secondary text-[var(--easy)]' : 'btn-primary',
                )}
              >
                {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                {done ? t('courses.completed') : t('courses.markComplete')}
              </button>
            )}
          </div>

          {current.contentMd && (
            <section className="mt-8">
              <h2 className="mb-3 text-lg font-semibold">{t('courses.notes')}</h2>
              <MarkdownLite text={current.contentMd} />
            </section>
          )}

          {current.exercises.length > 0 && (
            <section className="mt-8">
              <h2 className="text-lg font-semibold">{t('courses.exercisesTitle')}</h2>
              <p className="mb-3 mt-1 text-sm text-content-secondary">{t('courses.exercisesHint')}</p>
              <ul className="card divide-y divide-line">
                {current.exercises.map((ex) => (
                  <li key={ex.slug}>
                    <Link
                      to={`/problems/${ex.slug}`}
                      className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-surface"
                    >
                      <span className="min-w-0 flex-1 truncate font-medium">{ex.title}</span>
                      {ex.premium && <Lock className="h-3.5 w-3.5 text-[var(--medium)]" aria-label="Premium" />}
                      <DifficultyBadge difficulty={mapDifficulty(ex.difficulty)} />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <nav className="mt-10 grid gap-3 border-t border-line pt-6 sm:grid-cols-2" aria-label={t('courses.curriculum')}>
            {current.prev ? (
              <Link
                to={lessonPath(slug, current.prev.slug)}
                className="card card-hover flex items-center gap-3 p-4 text-sm"
              >
                <ArrowLeft className="h-4 w-4 shrink-0 text-content-muted" />
                <span className="min-w-0">
                  <span className="block text-xs text-content-muted">{t('courses.prev')}</span>
                  <span className="block truncate font-medium">{current.prev.title}</span>
                </span>
              </Link>
            ) : (
              <span />
            )}
            {current.next && (
              <Link
                to={lessonPath(slug, current.next.slug)}
                className="card card-hover flex items-center justify-end gap-3 p-4 text-right text-sm"
              >
                <span className="min-w-0">
                  <span className="block text-xs text-content-muted">{t('courses.next')}</span>
                  <span className="block truncate font-medium">{current.next.title}</span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-content-muted" />
              </Link>
            )}
          </nav>
        </main>

        {course && (
          <aside className="lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto">
            <div className="mb-3 flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-content-muted">
                {t('courses.backToCourse')}
              </h2>
            </div>
            <CourseCurriculum
              course={course}
              completed={completed}
              currentLesson={current.slug}
              compact
              hasAccess={hasCourse(slug)}
            />
          </aside>
        )}
      </div>
    </div>
  );
}
